
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/header/ModernHeader";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/SEO/Breadcrumbs";
import StructuredData from "@/components/SEO/StructuredData";
import { useSEO } from "@/hooks/useSEO";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { generateProductSchema } from "@/utils/seoUtils";
import { extractProductIdentifier } from "@/utils/productUrlUtils";
import { useEnhancedProductData } from "@/hooks/useEnhancedProductData";
import { useState } from "react";
import { WriteReviewDialog } from "@/components/ProductDetail/WriteReviewDialog";

// Enhanced components
import ProductDetailSkeleton from "@/components/ProductDetail/ProductDetailSkeleton";
import ProductNotFound from "@/components/ProductDetail/ProductNotFound";
import LuxuryProductHeader from "@/components/ProductDetail/LuxuryProductHeader";
import LuxuryProductImageGallery from "@/components/ProductDetail/LuxuryProductImageGallery";
import EnhancedProductVariantSelector from "@/components/ProductDetail/EnhancedProductVariantSelector";
import EnhancedProductActions from "@/components/ProductDetail/EnhancedProductActions";
import ProductSpecificationsSection from "@/components/ProductDetail/ProductSpecificationsSection";
import ProductReviewsSection from "@/components/ProductDetail/ProductReviewsSection";
import ProductRecommendationsSection from "@/components/ProductDetail/ProductRecommendationsSection";
import ModernProductTabs from "@/components/ProductDetail/ModernProductTabs";
import { useModernProductContent } from "@/hooks/useModernProductContent";
import { ProductVariant } from "@/types/product";
import { ProductErrorBoundary } from "@/components/error/ProductErrorBoundary";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: siteSettings } = useSiteSettings();
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  // Extract and validate the product identifier
  const { isSlug, identifier } = id ? extractProductIdentifier(id) : { isSlug: false, identifier: '' };
  const isValidId = identifier && identifier !== 'null' && identifier !== 'undefined' && typeof identifier === 'string';

  // Use enhanced product data hook with slug/ID detection
  const { data: product, isLoading, error } = useEnhancedProductData(isValidId ? identifier : '', isSlug);
  
  console.log('🏁 ProductDetail rendered', { id, hasProduct: !!product, isLoading, error });
  
  // Fetch modern product content
  const { data: productContent } = useModernProductContent(product?.id || '');

  // SEO setup for product page
  useSEO({
    pageType: 'product',
    pageId: identifier,
    fallbackTitle: `${product?.name} - Best Price Online | VRAM Store`,
    fallbackDescription: 
      `Buy ${product?.name} at the best price. Fast shipping, quality guaranteed. Shop now at VRAM Store.`,
    customSchema: product && siteSettings ? generateProductSchema(product, siteSettings) : null
  });

  const handleBuyNow = () => {
    // Navigate to checkout with product
    navigate('/checkout', {
      state: {
        directPurchase: true,
        product: selectedVariant || product,
        quantity: 1
      }
    });
  };

  // Generate breadcrumbs
  const breadcrumbItems = [
    { label: 'Products', href: '/products' },
    ...(product?.categories?.name ? [{ label: product.categories.name }] : []),
    { label: product?.name || 'Product', active: true }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ProductDetailSkeleton />
      </div>
    );
  }

  if (error || !product || !isValidId) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ProductNotFound />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 text-foreground transition-colors duration-200">
      <Header />
      
      <StructuredData 
        data={product && siteSettings ? generateProductSchema(product, siteSettings) : null} 
        id="product-schema" 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-8 hover:bg-muted/50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <ProductErrorBoundary>
            <LuxuryProductImageGallery
              mainImage={selectedVariant?.image_url || product?.image_url}
              galleryImages={product?.images?.map(img => img.image_url) || product?.gallery_images}
              productName={product?.name || 'Product'}
            />
          </ProductErrorBoundary>

          <div className="space-y-8">
            <ProductErrorBoundary>
              <LuxuryProductHeader product={product} />
            </ProductErrorBoundary>
            
            {/* Variant Selection */}
            {product.variants && product.variants.length > 0 && (
              <ProductErrorBoundary>
                <EnhancedProductVariantSelector
                  variants={product.variants}
                  selectedVariant={selectedVariant}
                  onVariantChange={setSelectedVariant}
                />
              </ProductErrorBoundary>
            )}

            {/* Enhanced Actions */}
            <ProductErrorBoundary>
              <EnhancedProductActions
                product={product}
                selectedVariant={selectedVariant}
                onBuyNow={handleBuyNow}
              />
            </ProductErrorBoundary>
          </div>
        </div>

        {/* Modern Product Content Tabs */}
        <div className="mb-16">
          <ModernProductTabs
            productId={product.id}
          />
        </div>

        {/* Fallback: Legacy specifications if no modern content */}
        {(!productContent?.sections || productContent.sections.length === 0) && product.specifications && product.specifications.length > 0 && (
          <div className="mb-16">
            <ProductSpecificationsSection specifications={product.specifications} />
          </div>
        )}

        {/* Product Reviews */}
        {product.reviews && (
          <div className="mb-16">
            <ProductReviewsSection
              reviews={product.reviews}
              averageRating={product.average_rating || 0}
              reviewCount={product.review_count || 0}
            />
          </div>
        )}

        {/* Product Recommendations */}
        {product.recommendations && product.recommendations.length > 0 && (
          <div className="space-y-16">
            <ProductRecommendationsSection
              recommendations={product.recommendations}
              type="related"
            />
            
            <ProductRecommendationsSection
              recommendations={product.recommendations}
              type="frequently_bought_together"
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
